import { useMemo } from 'react';
import { Activity } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, StatusPill, Stat, StatGroup, BarList, type ColumnDef } from '@vnetwork/ui/data';
import { Badge, Skeleton, Card, CardHeader, CardTitle, CardBody } from '@vnetwork/ui/primitives';
import styles from './ClusterMetrics.module.css';
import {
  useClusterMetrics,
  cpuToMillicores,
  memToMiB,
  type PodMetric,
  type NodeMetric,
} from '@/api/k8s';

interface PodRow { name: string; namespace: string; cpu: number; mem: number }
interface NodeRow { name: string; status: string; cpu: number; mem: number; pods: number }

const podUsage = (p: PodMetric) => ({
  cpu: (p.containers ?? []).reduce((s, c) => s + cpuToMillicores(c.usage?.cpu), 0),
  mem: (p.containers ?? []).reduce((s, c) => s + memToMiB(c.usage?.memory), 0),
});

/** Rich metrics dashboard: cluster summary + node-level usage + pod↔node
 *  distribution + per-namespace breakdown + pod usage table. Mirrors the
 *  @console ManageMetrics surfaces on the v2 design system. */
export function MetricsTab({ serviceId, clusterId }: { serviceId: string; clusterId: string }) {
  const { data: metrics, isLoading } = useClusterMetrics(serviceId, clusterId);
  const pods = metrics?.pods ?? [];
  const nodes = metrics?.nodes ?? [];
  const namespaces = metrics?.namespaces ?? {};
  const dist = metrics?.node_pod_distribution;

  const podRows = useMemo<PodRow[]>(() => pods.map((p) => {
    const u = podUsage(p);
    return { name: p.metadata?.name ?? '—', namespace: p.metadata?.namespace ?? '—', cpu: u.cpu, mem: u.mem };
  }), [pods]);

  const podsOnNode = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of dist?.distribution ?? []) m.set(d.node_name, d.pod_count);
    return m;
  }, [dist]);

  const nodeRows = useMemo<NodeRow[]>(() => nodes.map((n: NodeMetric) => {
    const name = n.metadata?.name ?? '—';
    return {
      name,
      status: dist?.distribution.find((d) => d.node_name === name)?.node_status ?? 'Ready',
      cpu: cpuToMillicores(n.usage?.cpu),
      mem: memToMiB(n.usage?.memory),
      pods: podsOnNode.get(name) ?? 0,
    };
  }), [nodes, dist, podsOnNode]);

  const totalCpu = podRows.reduce((s, r) => s + r.cpu, 0);
  const totalMem = podRows.reduce((s, r) => s + r.mem, 0);
  const maxCpu = Math.max(1, ...podRows.map((r) => r.cpu));
  const maxMem = Math.max(1, ...podRows.map((r) => r.mem));
  const totalPods = dist?.total_pods ?? podRows.length;

  // Per-namespace summary (cpu/mem/pods), sorted by CPU.
  const byNs = useMemo(() => {
    const m = new Map<string, { pods: number; cpu: number; mem: number }>();
    const src = Object.keys(namespaces).length
      ? Object.entries(namespaces).flatMap(([ns, list]) => list.map((p) => ({ ns, ...podUsage(p) })))
      : podRows.map((r) => ({ ns: r.namespace, cpu: r.cpu, mem: r.mem }));
    for (const r of src) {
      const e = m.get(r.ns) ?? { pods: 0, cpu: 0, mem: 0 };
      e.pods++; e.cpu += r.cpu; e.mem += r.mem; m.set(r.ns, e);
    }
    return [...m.entries()].map(([namespace, v]) => ({ namespace, ...v })).sort((a, b) => b.cpu - a.cpu);
  }, [namespaces, podRows]);

  // Distribution balance (variance / average), matching @console thresholds.
  const balance = useMemo(() => {
    if (!dist) return null;
    const { min_pods_on_node, max_pods_on_node, average_pods_per_node } = dist.summary;
    const ratio = average_pods_per_node > 0 ? (max_pods_on_node - min_pods_on_node) / average_pods_per_node : 0;
    if (ratio < 0.2) return { tone: 'success' as const, label: 'Well balanced' };
    if (ratio < 0.5) return { tone: 'warning' as const, label: 'Moderately balanced' };
    return { tone: 'danger' as const, label: 'Unbalanced' };
  }, [dist]);

  const podCols: ColumnDef<PodRow, unknown>[] = [
    // Truncate long pod names to a single line so rows don't wrap.
    { id: 'name', header: 'Pod', cell: ({ row }) => <span title={row.original.name} style={{ fontWeight: 'var(--vn-font-weight-medium)' as React.CSSProperties['fontWeight'], ...truncate }}>{row.original.name}</span> },
    { id: 'ns', header: 'Namespace', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.namespace}</span> },
    { id: 'cpu', header: 'CPU', cell: ({ row }) => <Bar value={row.original.cpu} max={maxCpu} label={`${Math.round(row.original.cpu)}m`} /> },
    { id: 'mem', header: 'Memory', cell: ({ row }) => <Bar value={row.original.mem} max={maxMem} label={`${Math.round(row.original.mem)} Mi`} /> },
  ];

  const nodeCols: ColumnDef<NodeRow, unknown>[] = [
    // Truncate long node names to a single line so rows don't wrap.
    { id: 'name', header: 'Node', cell: ({ row }) => <span title={row.original.name} style={{ fontWeight: 'var(--vn-font-weight-medium)' as React.CSSProperties['fontWeight'], ...truncate }}>{row.original.name}</span> },
    // Map raw status to human-readable "Ready" / "Not ready" — never show raw "True"/"False".
    { id: 'status', header: 'Status', cell: ({ row }) => { const ready = /^(ready|true)$/i.test(row.original.status ?? ''); return <StatusPill tone={ready ? 'success' : 'muted'}>{ready ? 'Ready' : 'Not ready'}</StatusPill>; } },
    { id: 'cpu', header: 'CPU', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{Math.round(row.original.cpu)}m</span> },
    { id: 'mem', header: 'Memory', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{Math.round(row.original.mem)} Mi</span> },
    { id: 'pods', header: 'Pods', cell: ({ row }) => row.original.pods },
  ];

  if (isLoading) {
    return (
      <Section title="Metrics" description="Live cluster, node and pod resource usage (metrics-server).">
        <Skeleton style={{ height: 320 }} />
      </Section>
    );
  }
  if (podRows.length === 0 && nodeRows.length === 0) {
    return <Section title="Metrics"><EmptyState icon={<Activity size={20} />} title="No metrics yet" description="metrics-server reports usage once pods are running." /></Section>;
  }

  // Each zone is a framed panel (template grammar): unframed content — the KPI
  // StatGroups and the per-namespace BarList — lives inside a Card; the tables
  // keep their own DataTable frame under a matching panel title. Nothing floats
  // bare on the page background.
  return (
    <Section title="Metrics" description="Live cluster, node and pod resource usage (metrics-server).">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Card flat>
        <CardBody>
          <StatGroup columns={5} className={styles.statGroupFive}>
            <Stat label="Nodes" value={String(nodes.length)} />
            <Stat label="Pods" value={String(totalPods)} />
            <Stat label="Namespaces" value={String(byNs.length)} />
            <Stat label="Total CPU" value={`${Math.round(totalCpu)}m`} />
            <Stat label="Total memory" value={`${Math.round(totalMem)} Mi`} />
          </StatGroup>
        </CardBody>
      </Card>

      {dist && (
        <Card flat>
          <CardHeader actions={balance && <Badge tone={balance.tone} appearance="subtle" size="sm">{balance.label}</Badge>}>
            <CardTitle>Pod distribution</CardTitle>
          </CardHeader>
          <CardBody>
            <StatGroup columns={4} className={styles.statGroupFour}>
              <Stat label="Average pods / node" value={String(dist.summary.average_pods_per_node)} />
              <Stat label="Most loaded node" value={`${dist.summary.max_pods_on_node} pods`} />
              <Stat label="Least loaded node" value={`${dist.summary.min_pods_on_node} pods`} />
              <Stat label="Distribution range" value={`${dist.summary.min_pods_on_node}–${dist.summary.max_pods_on_node}`} />
            </StatGroup>
          </CardBody>
        </Card>
      )}

      {nodeRows.length > 0 && (
        <div>
          <div style={panelTitle}>Nodes ({nodeRows.length})</div>
          <DataTable columns={nodeCols} data={nodeRows} getRowId={(n, i) => `${n.name}/${i}`} footer={`Showing ${nodeRows.length} of ${nodeRows.length}`} />
        </div>
      )}

      {byNs.length > 0 && (
        <Card flat>
          <CardHeader><CardTitle>By namespace</CardTitle></CardHeader>
          <CardBody>
            <BarList items={byNs.map((n) => ({ label: n.namespace, value: Math.round(n.cpu), display: `${n.pods} pods · ${Math.round(n.cpu)}m · ${Math.round(n.mem)} Mi` }))} />
          </CardBody>
        </Card>
      )}

      {podRows.length > 0 && (
        <div>
          <div style={panelTitle}>Pods ({podRows.length})</div>
          <DataTable columns={podCols} data={podRows} getRowId={(r, i) => `${r.namespace}/${r.name}/${i}`} footer={`Showing ${podRows.length} of ${podRows.length}`} />
        </div>
      )}
      </div>
    </Section>
  );
}

// Clamp long resource names (node/pod) to a single line with an ellipsis; the
// full name is still accessible via `title` on the cell span.
const truncate: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: 220,
};

// Panel title above a bare (self-framed) DataTable — matches CardTitle weight so
// table panels read the same as the carded stat/bar panels.
const panelTitle: React.CSSProperties = {
  marginBottom: 'var(--vn-space-2)',
  fontSize: 'var(--vn-font-size-md)',
  fontWeight: 'var(--vn-font-weight-semibold)' as React.CSSProperties['fontWeight'],
};

function Bar({ value, max, label }: { value: number; max: number; label: string }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  return (
    <div className={styles.barCell}>
      <div className={styles.barTrack}>
        <div style={{ width: `${pct}%`, height: '100%', background: pct > 85 ? 'var(--vn-danger-solid)' : 'var(--vn-accent-solid)' }} />
      </div>
      <span className={styles.barLabel}>{label}</span>
    </div>
  );
}
